import React from "react";
import { useMusicPlayer } from "../context/MusicPlayerContext";
import NowPlaying from "./NowPlaying";
import {
  Play,
  Pause,
  SkipBack,
  SkipForward,
  Shuffle,
  Repeat,
  Repeat1,
  Heart,
  Maximize,
  Music as MusicIcon,
} from "lucide-react";

const formatTime = (seconds) => {
  if (!seconds || Number.isNaN(Number(seconds))) {
    return "0:00";
  }

  const mins = Math.floor(Number(seconds) / 60);
  const secs = Math.floor(Number(seconds) % 60);

  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

export default function GlobalMusicPlayer() {
  const {
    currentTrack,
    playing,
    progress,
    duration,
    seek,
    shuffle,
    setShuffle,
    repeat,
    setRepeat,
    nextTrack,
    prevTrack,
    togglePlay,
    showFull,
    setShowFull,
    favorites = [],
    addToFavorites,
    removeFromFavorites,
  } = useMusicPlayer();

  // Nothing is playing.
  if (!currentTrack) {
    return null;
  }

  const isFavorite = favorites.includes(currentTrack.id);

  const toggleFavorite = (e) => {
    e?.stopPropagation();

    if (isFavorite) {
      removeFromFavorites(currentTrack.id);
    } else {
      addToFavorites(currentTrack.id);
    }
  };

  const cycleRepeat = (e) => {
    e?.stopPropagation();

    setRepeat(
      repeat === "none"
        ? "all"
        : repeat === "all"
        ? "one"
        : "none"
    );
  };

  /*
   * FULL PLAYER
   *
   * NowPlaying is also rendered globally.
   * This means navigating between routes does not
   * destroy the audio/player state.
   */
  if (showFull) {
    return <NowPlaying />;
  }

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[100] bg-gray-950/95 backdrop-blur-xl border-t border-gray-800 shadow-2xl">
      {/* Progress line */}
      <div className="absolute top-0 left-0 right-0 h-[2px] bg-gray-800">
        <div
          className="h-full bg-blue-500 transition-[width] duration-200"
          style={{
            width: `${
              duration > 0
                ? Math.min((progress / duration) * 100, 100)
                : 0
            }%`,
          }}
        />
      </div>

      <div className="max-w-7xl mx-auto px-3 sm:px-6 py-2">
        <div className="flex items-center gap-3">
          {/* =========================
              TRACK INFO
          ========================= */}

          <button
            type="button"
            onClick={() => setShowFull(true)}
            className="flex items-center gap-3 min-w-0 flex-1 sm:flex-none sm:w-[260px] text-left group"
          >
            {currentTrack.cover || currentTrack.thumbnail ? (
              <img
                src={
                  currentTrack.cover ||
                  currentTrack.thumbnail
                }
                alt={currentTrack.title || "Track artwork"}
                className="w-12 h-12 sm:w-14 sm:h-14 rounded-md object-cover bg-gray-800 shrink-0"
              />
            ) : (
              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-md bg-gray-800 flex items-center justify-center shrink-0">
                <MusicIcon
                  size={22}
                  className="text-gray-500"
                />
              </div>
            )}

            <div className="min-w-0">
              <p className="font-semibold text-sm truncate group-hover:text-blue-400 transition">
                {currentTrack.title || "Untitled Track"}
              </p>

              <p className="text-xs text-gray-400 truncate">
                {currentTrack.artist || "Unknown Artist"}
              </p>
            </div>
          </button>

          {/* =========================
              DESKTOP CONTROLS
          ========================= */}

          <div className="hidden md:flex flex-1 flex-col items-center">
            <div className="flex items-center gap-5">
              {/* Shuffle */}
              <button
                type="button"
                onClick={() => setShuffle(!shuffle)}
                title="Shuffle"
                className={`transition ${
                  shuffle
                    ? "text-blue-400"
                    : "text-gray-400 hover:text-white"
                }`}
              >
                <Shuffle
                  size={18}
                  fill={shuffle ? "currentColor" : "none"}
                />
              </button>

              {/* Previous */}
              <button
                type="button"
                onClick={prevTrack}
                title="Previous"
                className="text-gray-300 hover:text-white transition"
              >
                <SkipBack
                  size={20}
                  fill="currentColor"
                />
              </button>

              {/* Play / Pause */}
              <button
                type="button"
                onClick={togglePlay}
                title={playing ? "Pause" : "Play"}
                className="w-9 h-9 rounded-full bg-white text-black flex items-center justify-center hover:scale-105 transition"
              >
                {playing ? (
                  <Pause
                    size={18}
                    fill="currentColor"
                  />
                ) : (
                  <Play
                    size={18}
                    fill="currentColor"
                    className="ml-0.5"
                  />
                )}
              </button>

              {/* Next */}
              <button
                type="button"
                onClick={nextTrack}
                title="Next"
                className="text-gray-300 hover:text-white transition"
              >
                <SkipForward
                  size={20}
                  fill="currentColor"
                />
              </button>

              {/* Repeat */}
              <button
                type="button"
                onClick={cycleRepeat}
                title="Repeat"
                className={`transition ${
                  repeat !== "none"
                    ? "text-blue-400"
                    : "text-gray-400 hover:text-white"
                }`}
              >
                {repeat === "one" ? (
                  <Repeat1 size={18} />
                ) : (
                  <Repeat
                    size={18}
                    fill={
                      repeat === "all"
                        ? "currentColor"
                        : "none"
                    }
                  />
                )}
              </button>
            </div>

            {/* Progress */}
            <div className="w-full max-w-xl flex items-center gap-2 mt-1">
              <span className="text-[10px] text-gray-500 font-mono w-9 text-right">
                {formatTime(progress)}
              </span>

              <input
                type="range"
                min={0}
                max={duration || 0}
                value={Math.min(progress || 0, duration || 0)}
                onChange={(e) =>
                  seek(Number(e.target.value))
                }
                className="flex-1 h-1 appearance-none rounded-full bg-gray-700 accent-blue-500 cursor-pointer"
              />

              <span className="text-[10px] text-gray-500 font-mono w-9">
                {formatTime(duration)}
              </span>
            </div>
          </div>

          {/* =========================
              RIGHT SIDE
          ========================= */}

          <div className="flex items-center gap-2 sm:gap-3">
            {/* Favorite */}
            <button
              type="button"
              onClick={toggleFavorite}
              title={
                isFavorite
                  ? "Remove from favorites"
                  : "Add to favorites"
              }
              className="p-2 text-gray-400 hover:text-white transition"
            >
              <Heart
                size={19}
                className={
                  isFavorite
                    ? "text-red-500 fill-red-500"
                    : ""
                }
              />
            </button>

            {/* Open full player */}
            <button
              type="button"
              onClick={() => setShowFull(true)}
              title="Open Now Playing"
              className="p-2 text-gray-400 hover:text-white transition"
            >
              <Maximize size={19} />
            </button>

            {/* Mobile play */}
            <button
              type="button"
              onClick={togglePlay}
              className="md:hidden w-9 h-9 rounded-full bg-white text-black flex items-center justify-center"
            >
              {playing ? (
                <Pause size={17} fill="currentColor" />
              ) : (
                <Play
                  size={17}
                  fill="currentColor"
                  className="ml-0.5"
                />
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
